import { ChangeDetectorRef, Component, NgModule, ViewChild } from '@angular/core';
import { SideMenuComponent } from '../../components/navbars/side-menu/side-menu.component';
import { NavbarComponent } from '../../components/navbars/navbar/navbar.component';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { CommonModule, DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { NgModel } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { ACustomerInfoComponent } from "../../components/forms/a-customer-info/a-customer-info.component";
import { CDocumentsFormComponent } from "../../components/forms/c-documents-form/c-documents-form.component";
import { BCategoryFormComponent } from "../../components/forms/b-category-form/b-category-form.component";
import { ETopCustomersComponent } from "../../components/forms/e-top-customers/e-top-customers.component";
import { FHealthSafetyComponent } from "../../components/forms/f-health-safety/f-health-safety.component";
import { DDirectorsAuthorityFormComponent } from "../../components/forms/d-directors-authority-form/d-directors-authority-form.component";
import { GTermsConditionsComponent } from "../../components/forms/g-terms-conditions/g-terms-conditions.component";
import { CsvDownloadService } from '../../Services/CSV Service/downloadcsv.service';
import { DataService } from '../../Services/Data Service/data.service';
import { AuditService } from '../../Services/Data Service/audit.service';
import {
  catchError,
  concatMap,
  filter,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
  throwError,
  timeout,
} from 'rxjs';
import { describeHttpError, formatEmailDeliveryUserMessage, NotificationService } from '../../Services/Data Service/notification.service';
import { FormUpdateService } from '../../Services/Form Data/formupdate.service';
import { AuthService } from '../../Auth/auth.service';
import { FormSubmitService } from '../../Services/Form Data/formsubmit.service';
import { FormDataService } from '../../Services/Form Data/form-data.service';
import { isAdminRole } from '../../roles';

@Component({
  selector: 'app-view-form-page',
  standalone: true,
  imports: [
    FormsModule, CommonModule,
    RouterOutlet, NavbarComponent,
    SideMenuComponent, ACustomerInfoComponent,
    CDocumentsFormComponent, NgFor, NgIf, NgClass,
    BCategoryFormComponent,
    ETopCustomersComponent,
    FHealthSafetyComponent,
    DDirectorsAuthorityFormComponent,
    GTermsConditionsComponent
],
providers: [DatePipe],
  templateUrl: './view-form-page.component.html',
  styleUrl: './view-form-page.component.css'
})

export class ViewFormPageComponent {

  title = "Registration Form";

  showCard: boolean = true;

  isNavbarExpanded = false;
  /** Full-page overlay for admin actions (approve/decline/etc.). */
  loading: boolean = false;
  /** True while flag/unflag is updating (avoids full-page overlay). */
  flagUpdating = false;
  /** True until logged-in user (and admin target user, if any) is resolved. */
  loadingInitial = true;
  /** True while company section payloads are still loading. */
  loadingFormDetails = false;
  loadError: string | null = null;

  readonly sectionSkeletonIndices = [0, 1, 2, 3, 4, 5];

  userId?: number;
  foundVendor: any;

  /** Vendor account shown on this page (admin target or logged-in vendor). */
  get viewUser(): any {
    return this.foundVendor ?? this.currUser;
  }

  get isAdminUser(): boolean {
    return isAdminRole(this.currUser?.role);
  }

  /** False until account status is safe to show (avoids stale session flash). */
  get isViewUserStatusReady(): boolean {
    if (this.loadingInitial) {
      return false;
    }
    if (this.isAdminUser && this.userId) {
      return !!this.foundVendor;
    }
    if (this.currUser?.role === 'Vendor') {
      return !this.loadingFormDetails;
    }
    return true;
  }

  get viewUserStatusLabel(): string {
    if (!this.isViewUserStatusReady) {
      return 'Loading';
    }
    return this.viewUser?.strAccountStatus ?? 'Loading';
  }

  get viewUserStatusPillClass(): Record<string, boolean> {
    if (!this.isViewUserStatusReady) {
      return { 'status-loading': true };
    }
    const status = this.viewUser?.strAccountStatus;
    return {
      'status-vendor': ['Vendor Action Required', 'Under Review'].includes(status),
      'status-active': status === 'Approved',
      'status-declined': status === 'Declined',
    };
  }

  toggleCollapse(expanded: boolean) {
    this.isNavbarExpanded = expanded;
  }
  
  currentPage = 0;

constructor(private downloadCsvService: CsvDownloadService, 
              private dataService: DataService,
              private auditService: AuditService,
              private route: ActivatedRoute,
              private cdr: ChangeDetectorRef,
              private notificationService: NotificationService,
              private formDataService: FormDataService, 
              private datePipe: DatePipe,
              private formUpdateService: FormUpdateService,
              private formSubmitService: FormSubmitService,
              private router: Router,
              private auth: AuthService
           ) { }

// // Sample requests for testing
//    requests = [
//     { 
//       id: 1, 
//       requestType: 'Support', 
//       status: 'Open', 
//       createdAt: new Date(), 
//       customer: 'John Doe' 
//     },
//   ];

//GET Current User's form submission
  submissiondate = '';
  statusdate = '';
  statusActor: any = null;
  currUser: any;
  currCompany: any;
  submittedData: any; //Instance of formData

  ngOnInit() {
    this.loadingInitial = true;
    this.loadError = null;
    this.checkDocumentExpiry();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      const parsed = Number(id);
      this.userId = Number.isFinite(parsed) ? parsed : undefined;
    }

    this.loadCurrUser();
  }

  /** True when access token and email exist in storage (same gate as app init). */
  private hasSession(): boolean {
    const token = this.auth.getAccessToken();
    const email = this.auth.getUserEmail();
    return !!(token && email);
  }

  /**
   * Wait for AppComponent to populate currentUser$ (child ngOnInit runs before parent).
   * Do not call getCurrentUser() on the initial null — that raced app init and triggered logout.
   */
  loadCurrUser() {
    if (!this.hasSession()) {
      this.failSessionLoad('Your session has expired. Please sign in again.', true);
      return;
    }

    this.dataService.currentUser$.pipe(
      filter((u): u is NonNullable<typeof u> => u != null && !!u.role),
      take(1),
      timeout({ first: 15000 }),
      catchError((err) => {
        if (!this.hasSession()) {
          return throwError(() => new Error('No access token found'));
        }
        return this.dataService.getCurrentUser().pipe(
          filter((u): u is NonNullable<typeof u> => u != null && !!u.role),
          take(1)
        );
      })
    ).subscribe({
      next: (loggedInUser) => this.onSessionUserReady(loggedInUser),
      error: (error) => {
        console.error('Error fetching logged-in user:', error);
        this.failSessionLoad('Could not load your session. Please sign in again.', true);
      },
    });
  }

  private onSessionUserReady(loggedInUser: any): void {
    this.currUser = loggedInUser;
    this.loadError = null;

    if (this.currUser.role === 'Vendor') {
      this.submissiondate = this.currUser.dteCreated;
      this.statusdate = this.currUser.strAccountStatusDate;
      this.loadStatusActor(this.currUser);
    }

    if (
      this.currUser.role === 'Vendor' &&
      this.currUser.strAccountStatus === 'Vendor Action Required'
    ) {
      this.loadRequests(this.currUser.company.intCompanyPK);
    }

    if (this.isAdminUser && this.userId) {
      this.findTargetUser(this.userId);
    } else if (this.isAdminUser && !this.userId) {
      this.endInitialLoad();
      this.loadError =
        'No vendor was specified. Open a registration form from Vendor Management or Dashboard.';
      this.scheduleDetectChanges();
    } else {
      this.loadingFormDetails = true;
      this.endInitialLoad();
      this.loadUserData(this.currUser);
    }
  }

  private endInitialLoad(): void {
    queueMicrotask(() => {
      this.loadingInitial = false;
      this.scheduleDetectChanges();
    });
  }

  private scheduleDetectChanges(): void {
    queueMicrotask(() => this.cdr.detectChanges());
  }

  private failSessionLoad(message: string, redirectToLogin: boolean): void {
    this.loadError = message;
    queueMicrotask(() => {
      this.loadingInitial = false;
      this.scheduleDetectChanges();
      if (redirectToLogin) {
        void this.router.navigate(['/login'], { replaceUrl: true });
      }
    });
  }

  /** Treat missing company resources as empty so one 404 does not block the whole form. */
  private safeCompanyFetch<T>(obs: Observable<T>, fallback: T): Observable<T> {
    return obs.pipe(
      catchError((err) => {
        if (err?.status === 404) {
          return of(fallback);
        }
        return throwError(() => err);
      })
    );
  }

  private emptySubmittedDataShell(user: any, company: any) {
    return {
      user: { ...user, signatureFile: null },
      company: { ...company },
      documents: {},
      companyBusTypes: [] as any[],
      directors: [] as any[],
      authsignatories: [] as any[],
      subsidaries: [] as any[],
      strPrimaryWorkSubContracted: [] as string[],
      blnAllowsSubcontracts: false,
      blnContractStatus: false,
      strContractDetails: '',
      topcustomers: [] as any[],
      hse_docs: { agreedToSustainabilityChecklist: false },
      vendorConsentRecord: null as {
        accepted: boolean;
        consentVersion: string;
        consentText: string;
        acceptedAt: string;
      } | null,
      vendorConsentAbsent: false,
      vendorConsentLoaded: false,
    };
  }

  private applyFormSectionResponses(
    user: any,
    company: any,
    responses: {
      businessTypes: any[];
      contracts: any[];
      contractDetails: any[];
      directors: any[];
      documents: any;
      hseDocs: any;
      signatories: any[];
      subcontracts: any[];
      subsidiaries: any[];
      vendorConsent: {
        record: {
          accepted: boolean;
          consentVersion: string;
          consentText: string;
          acceptedAt: string;
        } | null;
        absent: boolean;
      };
    }
  ) {
    this.submittedData = {
      user: { ...user, signatureFile: null },
      company: { ...company },
      documents: responses.documents || {},
      companyBusTypes: responses.businessTypes || [],
      directors: responses.directors || [],
      authsignatories: responses.signatories || [],
      subsidaries: responses.subsidiaries || [],
      strPrimaryWorkSubContracted:
        responses.subcontracts?.map((s: { strPrimaryWorkSubContracted: string }) => s.strPrimaryWorkSubContracted) ||
        [],
      blnAllowsSubcontracts: responses.contractDetails?.[0]?.blnAllowsSubcontracts || false,
      blnContractStatus: responses.contractDetails?.[0]?.blnContractStatus || false,
      strContractDetails: responses.contractDetails?.[0]?.strContractDetails || '',
      topcustomers: responses.contracts || [],
      hse_docs: {
        agreedToSustainabilityChecklist: false,
        ...(Array.isArray(responses.hseDocs) && responses.hseDocs.length > 0
          ? responses.hseDocs[0]
          : responses.hseDocs && !Array.isArray(responses.hseDocs)
            ? responses.hseDocs
            : {}),
      },
      vendorConsentRecord: responses.vendorConsent?.record ?? null,
      vendorConsentAbsent: responses.vendorConsent?.absent === true,
      vendorConsentLoaded: true,
    };
    this.formDataService.updateFormData(this.submittedData);
  }

  private showSnackbar(message: string): void {
    const snackbar = document.getElementById('snackbar');
  
    if (snackbar) {
      const snackbarElement = snackbar as HTMLElement & { timeoutId?: number };
  
      if (snackbarElement.timeoutId) {
        clearTimeout(snackbarElement.timeoutId);
      }
  
      snackbarElement.style.visibility = 'visible';
      snackbarElement.style.opacity = '1';
      snackbarElement.innerText = message;
      snackbarElement.classList.add('show');
  
      snackbarElement.timeoutId = setTimeout(() => {
        snackbarElement.style.visibility = 'hidden';
        snackbarElement.style.opacity = '0';
        snackbarElement.classList.remove('show');
      }, 3000) as unknown as number;
    }
  }
    
  private checkDocumentExpiry(){
    
  }

  private findTargetUser(userId: number) {
    this.loadError = null;
    this.dataService.getUserByPK(userId).subscribe({
      next: (targetUser) => {
        if (!targetUser?.company?.intCompanyPK) {
          console.error('Vendor not found or missing company for user PK:', userId);
          this.loadError = 'Vendor registration form could not be found.';
          this.endInitialLoad();
          this.cdr.markForCheck();
          return;
        }
        this.foundVendor = targetUser;
        this.submissiondate = targetUser.dteCreated;
        this.statusdate = targetUser.strAccountStatusDate;
        this.loadStatusActor(targetUser);
        this.loadNotes(targetUser.company.intCompanyPK);
        this.loadRequests(targetUser.company.intCompanyPK);
        this.loadUserData(targetUser);
        this.endInitialLoad();
      },
      error: (err) => {
        console.error('Could not fetch target user:', err);
        this.loadError = 'Could not load this vendor registration form. Please try again.';
        this.endInitialLoad();
        this.cdr.markForCheck();
      },
    });
  }

  private loadUserData(user: any) {
    const companyId = user?.company?.intCompanyPK;
    if (!companyId) {
      this.loadError = 'Vendor company information is missing.';
      this.cdr.markForCheck();
      return;
    }

    this.loadError = null;
    this.loadingFormDetails = true;
    this.cdr.markForCheck();

    this.dataService.getCompanyByPK(companyId).subscribe({
      next: (company) => {
        if (!company) {
          this.loadingFormDetails = false;
          this.loadError = 'Company record could not be found for this vendor.';
          this.cdr.markForCheck();
          return;
        }

        this.currCompany = company;
        this.formDataService.setCurrCompanyID(companyId);
        this.submittedData = this.emptySubmittedDataShell(user, company);
        this.formDataService.updateFormData(this.submittedData);
        this.cdr.markForCheck();

        forkJoin({
          businessTypes: this.safeCompanyFetch(
            this.dataService.getCompanyBusinessTypes(companyId),
            [] as any[]
          ),
          contracts: this.safeCompanyFetch(
            this.dataService.getCompanyContracts(companyId),
            [] as any[]
          ),
          contractDetails: this.safeCompanyFetch(
            this.dataService.getCompanyContractDetails(companyId),
            [] as any[]
          ),
          directors: this.safeCompanyFetch(
            this.dataService.getCompanyDirectors(companyId),
            [] as any[]
          ),
          documents: this.safeCompanyFetch(
            this.dataService.getCompanyDocuments(companyId),
            {} as any
          ),
          hseDocs: this.safeCompanyFetch(this.dataService.getCompanyHSE(companyId), [] as any[]),
          signatories: this.safeCompanyFetch(
            this.dataService.getCompanySignatories(companyId),
            [] as any[]
          ),
          subcontracts: this.safeCompanyFetch(
            this.dataService.getCompanySubcontracts(companyId),
            [] as any[]
          ),
          subsidiaries: this.safeCompanyFetch(
            this.dataService.getCompanySubsidiaries(companyId),
            [] as any[]
          ),
          vendorConsent: this.safeCompanyFetch(
            this.dataService.getVendorConsent(companyId),
            { record: null, absent: false }
          ),
        })
          .pipe(
            finalize(() => {
              this.loadingFormDetails = false;
              this.cdr.markForCheck();
            })
          )
          .subscribe({
            next: (responses) => {
              this.applyFormSectionResponses(user, company, responses);
              this.cdr.markForCheck();
            },
            error: (error) => {
              console.error('Error fetching related company data:', error);
              this.loadError =
                'Some registration sections could not be loaded. Refresh the page or try again later.';
              this.cdr.markForCheck();
            },
          });
      },
      error: (error) => {
        console.error('Error fetching company:', error);
        this.loadingFormDetails = false;
        this.loadError = 'Could not load company information for this vendor.';
        this.cdr.markForCheck();
      },
    });
  }
  
    
  flagForFollowUp(account: any) {
    if (this.flagUpdating) {
      return;
    }
    this.flagUpdating = true;
    this.auditService.submitAuditLog("Flagged vendor: " + account.strName);
    this.dataService.updateFlagged(account.intUserPK, true).subscribe({
      next: () => {
        account.blnFlagged = true;
        this.cdr.detectChanges();
        this.submittedData = [];

        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
          this.findTargetUser(Number(id));
        }
        this.flagUpdating = false;
      },
      error: (err) => {
        console.error('[flagForFollowUp] Failed to update status', err);
        this.flagUpdating = false;
      }
    });
  }
  
  unflagAccount(account: any) {
    if (this.flagUpdating) {
      return;
    }
    this.flagUpdating = true;
    this.auditService.submitAuditLog("Unflagged vendor: " + account.strName);
    this.dataService.updateFlagged(account.intUserPK, false).subscribe({
      next: () => {
        account.blnFlagged = false;
        this.cdr.detectChanges();
        this.submittedData = [];

        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
          this.findTargetUser(Number(id));
        }
        this.flagUpdating = false;
      },
      error: (err) => {
        this.flagUpdating = false;
        console.error('[unflagAccount] Failed to update status', err);
      }
    });
  }

//this was when we wanted to hide follow up status from vendor. now flags are private
  // get modifiedStatus() {
  //   if (this.currUser?.role === 'Vendor' && this.submittedData.user?.strAccountStatus === 'Follow Up') {
  //     return 'Under Review';
  //   }
  //   return this.submittedData.user?.strAccountStatus;
  // }
  

  notes: any[] = [];
  notifications: any[] = [];
  newNote: string = '';
  showNoteConfirmModal = false;
  private pendingNoteCompanyId: number | null = null;

  loadNotes(companyId: number) {
    this.dataService.getCompanyNotes(companyId).pipe(
      switchMap((notes) => {
        const adminIds = [...new Set(notes.map((n) => n.adminId).filter((id): id is number => id != null))];
        if (adminIds.length === 0) {
          this.notes = notes;
          return of(notes);
        }
        return forkJoin(adminIds.map((id) => this.dataService.getUserByPK(id))).pipe(
          map((users) => {
            const userMap = new Map<number, any>();
            adminIds.forEach((id, i) => userMap.set(id, users[i]));
            notes.forEach((note) => {
              note.creator = userMap.get(note.adminId);
            });
            this.notes = notes;
            return notes;
          })
        );
      })
    ).subscribe({
      error: (err) => console.error('[loadNotes] Failed to load notes or creators', err),
    });
  }
  
  latestNoti: any;
  showHistory = false;
  loadingNotes = false;

  loadRequests(companyId: number) {
    this.loadingNotes = true;
    this.notificationService.getNotifications(companyId).subscribe({
      next: (data) => {
        console.log("Notifications loaded: ", data);

        const actionRequiredNotis = data
          .filter((n) => n.type === 'action_required')
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

        this.latestNoti = actionRequiredNotis[0] ?? null;

        const adminIds = [...new Set(actionRequiredNotis.map((n) => n.adminId).filter((id): id is number => id != null))];
        if (adminIds.length === 0) {
          this.notifications = actionRequiredNotis;
          this.loadingNotes = false;
          console.log("Request:", this.latestNoti);
          return;
        }

        forkJoin(adminIds.map((id) => this.dataService.getUserByPK(id))).subscribe({
          next: (users) => {
            const userMap = new Map<number, any>();
            adminIds.forEach((id, i) => userMap.set(id, users[i]));
            actionRequiredNotis.forEach((noti) => {
              noti.creator = userMap.get(noti.adminId);
            });
            if (this.latestNoti) {
              this.latestNoti.creator = userMap.get(this.latestNoti.adminId);
            }
            this.notifications = actionRequiredNotis;
            this.loadingNotes = false;
            console.log("Request:", this.latestNoti);
          },
          error: (err) => {
            console.error('[loadRequests] Failed to load creators', err);
            this.loadingNotes = false;
          },
        });
      },
      error: (err) => {
        console.error('[loadRequests] Failed to load notifications', err);
        this.loadingNotes = false;
      },
    });
  }
  
  
  tempCompany: any;

  openNoteConfirmModal(companyId: number) {
    if (!this.newNote.trim() || companyId == null) return;
    this.pendingNoteCompanyId = companyId;
    this.showNoteConfirmModal = true;
  }

  cancelNoteConfirm() {
    this.showNoteConfirmModal = false;
    this.pendingNoteCompanyId = null;
  }

  confirmSubmitNote() {
    const companyId = this.pendingNoteCompanyId;
    if (companyId == null || !this.newNote.trim()) {
      this.cancelNoteConfirm();
      return;
    }
    this.showNoteConfirmModal = false;
    this.pendingNoteCompanyId = null;
    this.submitNote(companyId);
  }

  submitNote(companyId: number) {
    this.loadingNotes = true;

    if (!this.newNote.trim()) return;

    this.dataService.getCompanyByPK(companyId).subscribe({
      next: (company) => {
        this.auditService.submitAuditLog("Added new note to " + company.strCompanyName);
        this.dataService.addCompanyNote(
          companyId,
          this.newNote,
          this.currUser.intUserPK,
          this.submittedData?.user?.intUserPK
        ).subscribe(() => {
          this.newNote = '';
          this.loadingNotes = false;
          this.loadNotes(companyId); // refresh notes
        });
      },
      error: () => { this.loadingNotes = false; }
    });
  }

  formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleString();
  }

  shouldShowStatusMeta(user: any): boolean {
    const status = user?.strAccountStatus;
    return !!status && status !== 'Under Review';
  }

  formatStatusTimestamp(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    // Prefer consistent app formatting; gracefully fall back if parsing fails.
    const transformed = this.datePipe.transform(dateStr, 'MMM d, y, h:mm a');
    return transformed ?? this.formatDate(dateStr);
  }

  private loadStatusActor(user: any) {
    const actorId = user?.intAccountStatusUpdatedBy;
    if (!actorId || typeof actorId !== 'number') {
      this.statusActor = null;
      return;
    }
    this.dataService.getUserByPK(actorId).pipe(take(1)).subscribe({
      next: (actor) => {
        this.statusActor = actor;
      },
      error: (err) => {
        console.error('[loadStatusActor] Failed to load status actor', err);
        this.statusActor = null;
      }
    });
  }

  deleteNote(noteId: number) {
    if (confirm('Are you sure you want to delete this note?')) {
      this.auditService.submitAuditLog("Deleted a note.");
      this.dataService.deleteCompanyNote(noteId).subscribe(() => {
        this.notes = this.notes.filter(note => note.id !== noteId);
      });
    }
  }
  
  sendStatusNotification(companyId: number, adminId: number, type: 'approved' | 'declined') {
    const defaultMessages = {
      approved: 'Your registration form has been APPROVED! You are now an approved vendor.',
      declined: 'We regret to inform you that your registration form has been declined.'
    };
  
    const payload = {
      companyId,
      adminId,
      type,
      message: defaultMessages[type],
      createdAt: new Date(this.dataService.getLocalDate()), //is overwritten in backend. date is set in api
      fieldNames: [] 
    };


    const subject = {
      approved: 'UNIPET Vendor Registratation Form APPROVED!',
      declined: 'UNIPET Vendor Registration Form Declined'
    };

    this.notificationService.sendNotification(payload).pipe(
      switchMap(() => this.notificationService.sendEmailNotification("vendor-status", subject[type], this.foundVendor.strEmail))
    ).subscribe({
      next: (emailRes) => {
        const w = formatEmailDeliveryUserMessage(emailRes, 'notification');
        if (w) {
          this.showSnackbar(w);
        } else {
          this.showSnackbar('Notification sent.');
        }
      },
      error: err => {
        console.error('Error sending email notification:', err);
        this.showSnackbar(describeHttpError(err));
      }
    });
  }
  
  approve(account: any) {
    this.loading = true;
    if (confirm('Are you sure you want to approve this Vendor?')) {
      this.auditService.submitAuditLog("Approved vendor: " + account.strName);
      this.dataService.updateAccountStatus(account.intUserPK, 'Approved').subscribe({
        next: () => {
          this.cdr.detectChanges();
          this.submittedData = [];

          const id = this.route.snapshot.paramMap.get('id');
          if (id) {
            this.findTargetUser(Number(id));
          }

          this.sendStatusNotification(
            account.company.intCompanyPK,
            this.currUser.intUserPK,
            'approved'
          );

          this.loading = false;
        },
        error: (err) => {
          console.error('[approve] Failed to update status', err);
          this.loading = false;
        }
      });
    }
  }

  decline(account: any) {
    this.loading = true;
    if (confirm('Are you sure you want to Decline this Vendor?')) {
      this.auditService.submitAuditLog("Declined vendor: " + account.strName);
      this.dataService.updateAccountStatus(account.intUserPK, 'Declined').subscribe({
        next: () => {
          this.cdr.detectChanges();
          this.submittedData = [];

          const id = this.route.snapshot.paramMap.get('id');
          if (id) {
            this.findTargetUser(Number(id));
          }

          this.sendStatusNotification(
            account.company.intCompanyPK,
            this.currUser.intUserPK,
            'declined'
          );

          this.loading = false;
        },
        error: (err) => {
          console.error('[decline] Failed to update status', err);
          this.loading = false;
        }
      });
    }
  }

  sendBack(account: any) {
    this.loading = true;
    //pop up to ask for reasoning
    this.auditService.submitAuditLog("Sent back registration form to vendor: " + account.strName);

    this.dataService.updateAccountStatus(account.intUserPK, 'Vendor Action Required').subscribe({
      next: () => {
        this.cdr.detectChanges();
        this.submittedData = [];

        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
          this.findTargetUser(Number(id));
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('[sendBack] Failed to update status', err);
        this.loading = false;
      }
    });
  }

  showModal = false;
  commentText = '';

  openModal() {
    this.showModal = true;
  }

  closeModal() {
    this.error_message = '';
    this.selectedFields = [];
    this.showModal = false;
    this.commentText = '';
  }

  error_message = '';
  /** Inline error when resubmission or related API steps fail. */
  resubmit_error_message = '';
  formSections = [
    'Sec. 1: Vendor Information, Type of Organization and Business',
    'Sec. 2: Regulatory/Statutory Documents',
    'Sec. 3: Information on Directors, Authorized Persons, Affiliated/Holding Subs...',
    'Sec. 4: Major Customers, Subcontracts and Primary Works',
    'Sec. 5: Health & Safety, Sustainability Checklist',
    'Sec. 6: Signed T&Cs, Signed Code of Conduct and Ethics'
  ]
  
  selectedFields: string[] = [];

  // logSelectedFields() {
  //   console.log('Selected fields:', this.selectedFields);
  // }  


  removeType(index: number): void {
    this.selectedFields.splice(index, 1);  // Remove the item at the specified index
  }

  updateSelectedOptions(event: Event) {
    const selectedOptions = (event.target as HTMLSelectElement).selectedOptions;
    const selectedValues = Array.from(selectedOptions).map(option => option.value);

    this.selectedFields = [...new Set([...this.selectedFields, ...selectedValues])];
    console.log(this.selectedFields);
  }

  sendRequest() {
    if (!this.selectedFields || this.selectedFields.length === 0) {
      this.error_message = 'Please select at least one section that needs correction.';
      return;
    }
  
    // Procurement's Request: be able to send without reason
      if (!this.commentText || !this.commentText.trim()) {
        this.commentText = "Please review the following section(s)."
        // this.error_message = 'Please enter a reason.'
        // return;
      }

    // console.log(this.selectedFields);
    this.sendBack(this.submittedData.user) //changes the User's status

    //In-app notification
    const payload = {
      companyId: this.submittedData.user?.company.intCompanyPK,
      adminId: this.currUser.intUserPK, 
      message: this.commentText,
      type: 'action_required',
      createdAt: new Date(this.dataService.getLocalDate()), //is overwritten in backend. date is set in api
      fieldNames: this.selectedFields 
    };

    // console.log("Send Back payload: ", payload);

    //Send both in-app (request) & email notification 
    this.notificationService.sendNotification(payload).pipe( //in-app notification with field names
      switchMap(() => this.notificationService.sendEmailNotification( //email notificatiom
        "vendor-status", 
        'UNIPET Vendor Registration Form Action Required', 
        this.foundVendor.strEmail))
    ).subscribe({
      next: (emailRes) => {
        this.closeModal();
        const w = formatEmailDeliveryUserMessage(emailRes, 'notification');
        if (w) {
          this.showSnackbar(w);
        } else {
          this.showSnackbar('Notification sent.');
        }
      },
      error: err => {
        console.error('Error sending notification:', err);
        this.error_message = describeHttpError(err);
      }
    });
  }



  //RESUBMITTING OF FORM FROM VENDOR
  @ViewChild(ACustomerInfoComponent) aCustomerInfo!: ACustomerInfoComponent;
  @ViewChild(CDocumentsFormComponent) bDocumentsForm!: CDocumentsFormComponent;
  @ViewChild(DDirectorsAuthorityFormComponent) dDirectorsAuthorityForm!: DDirectorsAuthorityFormComponent;
  @ViewChild(ETopCustomersComponent) eTopCustomers!: ETopCustomersComponent;
  @ViewChild(FHealthSafetyComponent) fHealthSafety!: FHealthSafetyComponent;
  @ViewChild(GTermsConditionsComponent) gTermsConditions!: GTermsConditionsComponent;

  private sendResubmissionNotification(): Observable<any> {
    const payload = {
      companyId: 1, //receiver (UNIPET ID is always 1)
      adminId: this.currCompany.intCompanyPK, //sender
      message: `${this.currCompany.strCompanyName} resubmitted their form; confirming that the requested changes were made.`,
      type: 'resubmission',
      createdAt: new Date(this.dataService.getLocalDate()), //is overwritten in backend. date is set in api
      fieldNames: []
    };

    return this.notificationService.sendNotification(payload).pipe(
      switchMap(() =>
        this.notificationService.sendEmailNotification(
          "admin-submission",
          'Vendor Resubmitted their Registration Form',
          "procurement@unipet.co" //replace for testing
        )
      ),
      tap((emailRes) => {
        this.closeModal();
        const w = formatEmailDeliveryUserMessage(emailRes, 'submission');
        if (w) {
          this.showSnackbar(w);
        } else {
          this.showSnackbar('Form Resubmitted.');
        }
      }),
      catchError(err => {
        console.error('Error sending resubmission notification:', err);
        return throwError(() => err);
      })
    );
    
  }
  

  private updateUserStatus(): Observable<any> {
    this.loading = true;

    return this.dataService.updateAccountStatus(this.currUser.intUserPK, 'Under Review').pipe(
       tap(() => {
          console.log("User status updated.");
          this.currUser.strAccountStatus = 'Under Review';
          this.loading = false;
        }),
       catchError(err => {
          console.error('[resubmitted] Failed to update status', err);
          this.loading = false;
          return throwError(() => err);
        })
      );
  }

  submitUnlockedSections(): Observable<any> {
    const allFields = this.latestNoti.fieldNames
      .flatMap((f: string) => f.split(',').map((s: string) => s.trim()));

    if (allFields.includes('Vendor Information')) {
      this.aCustomerInfo.onSubmit();
    }
    if (allFields.includes('Regulatory/Statutory Documents')) {
      this.bDocumentsForm.onSubmit();
    }
    if (allFields.includes('Information on Directors')) {
      this.dDirectorsAuthorityForm.onSubmit();
    }
    if (allFields.includes('Major Customers')) {
      this.eTopCustomers.onSubmit();
    }
    if (allFields.includes('Health & Safety')) {
      this.fHealthSafety.onSubmit();
    }

    return of(void 0); // return a completed observable
  }

  
  resubmitForm() {
    this.loading = true;
    this.resubmit_error_message = '';
    console.log("Started resubmit.");
  
    if (!confirm('Are you sure that all changes were made? The document will no longer be editable once submitted.')) {
      return
    }

    this.submitUnlockedSections().pipe(
      concatMap(() => this.sendResubmissionNotification()), //send email & in app notification
      concatMap(() => this.updateUserStatus()), //change user status from action required to under review
    ).subscribe({
      next: () => {
        this.resubmit_error_message = '';
        this.latestNoti = null;
        this.loading = false;
        console.log('All updates completed.');
      },
      error: (err) => {
        this.loading = false;
        console.error('One or more update steps failed:', err);
        const msg = describeHttpError(err);
        this.resubmit_error_message = msg;
        this.showSnackbar(msg);
      }
    });

  }


    
// /**
//  * Downloads data as a CSV
//  * @param includeAllColumns Whether to include all columns or specific columns
//  */

downloadAsCsv() {
  if(this.currCompany){
    const fileName = `${this.currCompany.strCompanyName}_RegistrationForm_${new Date(this.dataService.getLocalDate())}.csv`;
    // if (includeAllColumns) {
    //   this.downloadCsvService.downloadCsv(this.submittedData, fileName);
    // } else {
      const headingMap: { [key: string]: string } = {
        // User
        'user.dteCreated': 'User Created Date',
        'user.company.intCompanyPK': 'Company ID',
        'user.role': 'User Role',
        'user.strAccountStatus': 'Account Status',
        'user.strAccountStatusDate': 'Date Account Status Changed',
        'user.strJobTitle': 'Job Title',
        'user.strContact': 'Phone Number',
        'user.strEmail': 'Email',
        'user.strName': 'Full Name',
        'user.signatureFile': 'Signature File',
      
        // Company
        'company.blnAffiliation': 'Is Affiliated',
        'company.dteEstablished': 'Date Established',
        'company.dteRegistration': 'Date Registered',
        'company.strnumEmployees': 'Number of Employees',
        'company.strAffiliation': 'Affiliation',
        'company.strCompanyName': 'Company Name',
        'company.strCompanyWebsite': 'Company Website',
        'company.strMailingAddress': 'Mailing Address',
        'company.strRegisteredAddress': 'Registered Address',
        'company.strOtherOrgType': 'Other Organization Type',
        'company.intCompanyOrgTypeFK': 'Organization Type ID',
      
        // Documents - VAT
        'documents.hasVatDocument': 'Has VAT Document',
        'documents.vatRegistrationNo': 'VAT Registration No.',
        'documents.noVatReason': 'Reason for No VAT',
        'documents.vatExpiryDate': 'VAT Expiry Date',
        'documents.vatFile': 'VAT Document File',
      
        // Documents - BIR
        'documents.hasBirDocument': 'Has BIR Document',
        'documents.birNo': 'BIR Number',
        'documents.noBirReason': 'Reason for No BIR',
        // 'documents.birExpiryDate': 'BIR Expiry Date',
        'documents.birFile': 'BIR Document File',
      
        // Documents - NIB
        'documents.hasNibDocument': 'Has NIB Document',
        'documents.nibNo': 'NIB Number',
        'documents.noNibReason': 'Reason for No NIB',
        'documents.nibExpiryDate': 'NIB Expiry Date',
        'documents.nibFile': 'NIB Document File',
      
        // Incorporation Certificate
        'documents.hasIncorporationCertificate': 'Has Incorporation Certificate',
        'documents.noIncorporationReason': 'Reason for No Incorporation Certificate',
        'documents.incorpFile': 'Incorporation File',
      
        // Registration Certificate
        'documents.hasRegistrationCertificate': 'Has Registration Certificate',
        'documents.regFile': 'Registration File',
      
        // Company Profile
        'documents.hasCompanyProfile': 'Has Company Profile',
        'documents.companyFile': 'Company Profile File',
      
        // General Licenses
        'documents.hasGeneralLicenses': 'Has General Licenses',
        'documents.generalLicensesExpiryDate': 'General Licenses Expiry Date',
        'documents.licenseFile': 'License File',
      
        // Company Business Types
        'companyBusTypes': 'Business Types',
      
        // Directors
        'directors[0].strName': 'Director 1 Name',
        'directors[0].strPosition': 'Director 1 Position',
        'directors[1].strName': 'Director 2 Name',
        'directors[1].strPosition': 'Director 2 Position',
      
        // Auth Signatories
        'authsignatories[0].strName': 'Auth Signatory Name',
        'authsignatories[0].strPosition': 'Auth Signatory Position',
        'authsignatories[0].strEmail': 'Auth Signatory Email',
        'authsignatories[0].strPhone': 'Auth Signatory Phone',
      
        // Subsidiaries
        'subsidaries[0].strName': 'Subsidiary Name',
        'subsidaries[0].strAddress': 'Subsidiary Address',
        'subsidaries[0].strAffiliation': 'Subsidiary Affiliation',
      
        // Work Subcontracted
        'strPrimaryWorkSubContracted': 'Primary Work Subcontracted',
      
        // Contract Details
        'blnAllowsSubcontracts': 'Subcontracts Work',
        'blnContractStatus': 'Has Active Contracts',
        'strContractDetails': 'Contract Details',
      
        // Top Customers
        'topcustomers[0].strPastCustomer': 'Top Customer Name',
        'topcustomers[0].dteYear': 'Top Customer Year',
        'topcustomers[0].strContractValue': 'Contract Value',
        'topcustomers[0].strContactPerson': 'Customer Contact Person',
        'topcustomers[0].strContactNumber': 'Customer Contact Number',
        'topcustomers[0].strContactEmail': 'Customer Email',
      
        // HSE Documents
        'hse_docs.hasSafetyFile': 'Has Safety File',
        'hse_docs.safetyFile': 'Safety File',
        'hse_docs.hasTrainingFile': 'Has Training File',
        'hse_docs.trainingFile': 'Training File',
        'hse_docs.hasResourcesFile': 'Has Resources File',
        'hse_docs.resourcesFile': 'Resources File'
      };
      
      
      const columnHeadings = [
        // User
        'user.dteCreated',
        'user.company.intCompanyPK',
        'user.role',
        'user.strAccountStatus',
        'user.strAccountStatusDate',
        'user.strJobTitle',
        'user.strContact',
        'user.strEmail',
        'user.strName',
        'user.signatureFile',
      
        // Company
        'company.blnAffiliation',
        'company.dteEstablished',
        'company.dteRegistration',
        'company.strnumEmployees',
        'company.strAffiliation',
        'company.strCompanyName',
        'company.strCompanyWebsite',
        'company.strMailingAddress',
        'company.strRegisteredAddress',
        'company.strOtherOrgType',
        'company.intCompanyOrgTypeFK',
      
        // Documents - VAT
        'documents.hasVatDocument',
        'documents.vatRegistrationNo',
        'documents.noVatReason',
        'documents.vatExpiryDate',
        'documents.vatFile',
      
        // Documents - BIR
        'documents.hasBirDocument',
        'documents.birNo',
        'documents.noBirReason',
        // 'documents.birExpiryDate',
        'documents.birFile',
      
        // Documents - NIB
        'documents.hasNibDocument',
        'documents.nibNo',
        'documents.noNibReason',
        'documents.nibExpiryDate',
        'documents.nibFile',
      
        // Incorporation Certificate
        'documents.hasIncorporationCertificate',
        'documents.noIncorporationReason',
        'documents.incorpFile',
      
        // Registration Certificate
        'documents.hasRegistrationCertificate',
        'documents.regFile',
      
        // Company Profile
        'documents.hasCompanyProfile',
        'documents.companyFile',
      
        // General Licenses
        'documents.hasGeneralLicenses',
        'documents.generalLicensesExpiryDate',
        'documents.licenseFile',
      
        // Company Business Types (as comma-separated list)
        'companyBusTypes',
      
        // Directors (flattened)
        'directors[0].strName',
        'directors[0].strPosition',
        'directors[1].strName',
        'directors[1].strPosition',
        // (add more if needed)
      
        // Auth Signatories (flattened)
        'authsignatories[0].strName',
        'authsignatories[0].strPosition',
        'authsignatories[0].strEmail',
        'authsignatories[0].strPhone',
        // (add more if needed)
      
        // Subsidiaries
        'subsidaries[0].strName',
        'subsidaries[0].strAddress',
        'subsidaries[0].strAffiliation',
        // (add more if needed)
      
        // Work Subcontracted
        'strPrimaryWorkSubContracted',
      
        // Contract Details
        'blnAllowsSubcontracts',
        'blnContractStatus',
        'strContractDetails',
      
        // Top Customers
        'topcustomers[0].strPastCustomer',
        'topcustomers[0].dteYear',
        'topcustomers[0].strContractValue',
        'topcustomers[0].strContactPerson',
        'topcustomers[0].strContactNumber',
        'topcustomers[0].strContactEmail',
      
        // HSE Documents
        'hse_docs.hasSafetyFile',
        'hse_docs.safetyFile',
        'hse_docs.hasTrainingFile',
        'hse_docs.trainingFile',
        'hse_docs.hasResourcesFile',
        'hse_docs.resourcesFile',
      ];
      if (!this.submittedData) {
        console.error('submittedData is missing or null!');
        return;
      }      
      const sanitizedData = JSON.parse(JSON.stringify(this.submittedData)); 
      if (sanitizedData.user?.strPassword) {
        delete sanitizedData.user.strPassword;
      }
      this.downloadCsvService.downloadCsv([sanitizedData], fileName, headingMap, columnHeadings);

      this.showSnackbar('Form downloaded');
    // }
  }
  else{
    console.error('currCompany not present');
    this.showSnackbar('Error downloading form.');
  }
}
}





